import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from '../../../../context/auth/auth-context'
import axios from 'axios'
import {
  type AlertColor, Button, Modal, Stack, Typography, useTheme, Grid, Card, CardContent, Chip, IconButton, Box, Switch,
  FormControlLabel, Divider,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../../../utils/time-utils'
import { formAlertErrorMessage, formAlertSuccessMessage } from '../../../../utils/string-utils'
import ToastWithMessage from '../../../../components/toast/toast-with-message'
import MiniLoader from '../../../../components/loader/mini-loader'
import ViewJSONTrigger from '../view/view-json-trigger'

interface AccountsModalProps {
  open: boolean
  onClose: () => void
  strategyId: string
  title: string
  dataUrl: string | null
  postUrl: string | null
}

interface Account {
  venueId: string
  account: string
  reduceOnly: boolean
  isPortfolioMargin: boolean
  active: boolean
  allowBuy: boolean
  allowSell: boolean
}

const AccountsModal: React.FC<AccountsModalProps> = (
  { open, onClose, strategyId, title, dataUrl, postUrl }
) => {
  const theme = useTheme()

  const [data, setData] = useState<Record<string, Record<string, Account>>>({})
  const [retrievalTimestamp, setRetrievalTimestamp] = useState<number>(0)
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})

  const { isAuthorized, accessToken } = useContext(AuthContext)

  const getAccounts = useCallback(() => {
    if (dataUrl != null && dataUrl.length !== 0) {
      axios
        .get(dataUrl, { headers: { Authorization: `Bearer ${accessToken}` } })
        .then(response => {
          const accounts: Account[] = response.data
          const groupedAccounts: Record<string, Record<string, Account>> = {}
          for (const account of accounts) {
            const venueId = account.venueId
            if (groupedAccounts[venueId] == null) {
              groupedAccounts[venueId] = {}
            }

            const internalMap = groupedAccounts[venueId]
            if (internalMap != null) {
              if (internalMap[account.account] != null) {
                console.error('Fatal error: duplicate account found')
                return
              }
              internalMap[account.account] = account
            }
          }

          setData(groupedAccounts)
          setRetrievalTimestamp(Date.now())
        })
        .catch(reason => {
          console.error(reason)
          setData({})
          setRetrievalTimestamp(-1)
        })
    }
  }, [isAuthorized, accessToken, dataUrl])

  useEffect(() => {
    if (open && isAuthorized) {
      getAccounts()
    }
  }, [open, isAuthorized])

  const postRequest = useCallback((venueAccount: string, message: object) => {
    if (postUrl == null || postUrl.length === 0) {
      console.debug('[Accounts Modal] Post url is empty')
      displayToastWithMessage('The POST url is invalid - please check the related configuration again', 'warning')
      return
    }

    console.debug(`[Accounts Modal] Queuing update request for the account: ${venueAccount}`)
    console.debug(message)

    axios
      .post(postUrl, message, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(response => {
        console.debug(response)
        displayToastWithMessage(formAlertSuccessMessage(response.status, response.statusText, response.data.message), 'success')
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
      })
      .finally(() => {
        getAccounts()
        setLoadingStates(prevState => { return { ...prevState, [venueAccount]: false } })
      })
  }, [strategyId, postUrl, accessToken])

  const updateReduceOnly = useCallback((venueId: string, account: string, reduceOnlyFlag: boolean) => {
    const message = {
      timestamp: new Date().toISOString(),
      strategyId,
      command: reduceOnlyFlag ? `enableReduceOnlyAccount,${venueId},${account}` : `disableReduceOnlyAccount,${venueId},${account}`,
    }
    postRequest(`${venueId}.${account}`, message)
  }, [postRequest, postUrl, strategyId])

  const updateAllowBuy = useCallback((venueId: string, account: string, allowBuyFlag: boolean) => {
    const message = {
      timestamp: new Date().toISOString(),
      strategyId,
      command: allowBuyFlag ? `enableBuyAccount,${venueId},${account}` : `disableBuyAccount,${venueId},${account}`,
    }
    postRequest(`${venueId}.${account}`, message)
  }, [postRequest, postUrl, strategyId])

  const updateAllowSell = useCallback((venueId: string, account: string, allowSellFlag: boolean) => {
    const message = {
      timestamp: new Date().toISOString(),
      strategyId,
      command: allowSellFlag ? `enableSellAccount,${venueId},${account}` : `disableSellAccount,${venueId},${account}`,
    }
    postRequest(`${venueId}.${account}`, message)
  }, [postRequest, postUrl, strategyId])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = useCallback((message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }, [strategyId])

  const onModalClose = useCallback(() => {
    setToastOpen(false)
    onClose()
  }, [onClose])

  const children = useMemo<React.ReactElement[]>(() => {
    return (
      Object.entries(data)
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
        .map(([venueId, mapValue]) => {
          const children = Object.entries(mapValue)
            .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
            .map(([account, accountValue]) => {
              const venueAccount = `${venueId}.${account}`
              return (
                <Card key={venueAccount} variant='outlined'>
                  <CardContent>
                    <Stack gap={1} padding={1}>
                      <Stack direction='row' gap={1} alignItems='center'>
                        <Typography fontSize='large' fontWeight='bold'>{account}</Typography>
                        {
                          accountValue.isPortfolioMargin
                            ? <Chip size='small' variant='outlined' label='Portfolio Margin'/>
                            : null
                        }
                        <Box marginX='auto'></Box>
                        {loadingStates[venueAccount] && <MiniLoader/>}
                      </Stack>

                      <Divider orientation='horizontal' flexItem/>

                      <FormControlLabel
                        label='Reduce Only'
                        control={
                          <Switch
                            checked={accountValue.reduceOnly}
                            onChange={(_event, checked) => {
                              setLoadingStates(prevState => { return { ...prevState, [venueAccount]: true } })
                              updateReduceOnly(venueId, account, checked)
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                            disabled={loadingStates[venueAccount]}
                          />
                        }
                      />

                      <Stack direction='row' gap={4}>
                        <FormControlLabel
                          label='Allow Buy'
                          control={
                            <Switch
                              checked={accountValue.allowBuy}
                              onChange={(_event, checked) => {
                                setLoadingStates(prevState => { return { ...prevState, [venueAccount]: true } })
                                updateAllowBuy(venueId, account, checked)
                              }}
                              inputProps={{ 'aria-label': 'controlled' }}
                              disabled={loadingStates[venueAccount]}
                            />
                          }
                        />

                        <FormControlLabel
                          label='Allow Sell'
                          control={
                            <Switch
                              checked={accountValue.allowSell}
                              onChange={(_event, checked) => {
                                setLoadingStates(prevState => { return { ...prevState, [venueAccount]: true } })
                                updateAllowSell(venueId, account, checked)
                              }}
                              inputProps={{ 'aria-label': 'controlled' }}
                              disabled={loadingStates[venueAccount]}
                            />
                          }
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              )
            })

          return (
            <Grid key={venueId} item xs={12} md={6} lg={3}>
              <Typography color='primary' fontWeight='bold' variant='h6' component='h2'>{venueId}</Typography>
              {children}
            </Grid>
          )
        })
    )
  }, [data])

  return (
    <Modal
      open={open}
      onClose={onModalClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Stack
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '1600px',
          height: '80%',
          maxHeight: '1000px',
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: 24,
          p: 4,
          color: 'white',
          overflowY: 'auto',
        }}
        gap={2}
      >
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography id='modal-modal-title' fontWeight='bold' variant='h5' component='h1'>
            {title}
          </Typography>
          <Chip
            label={formatToLocalMilliTimestampFromUTCNumber(retrievalTimestamp)}
            size='small'
          />
          <ViewJSONTrigger dataUrl={dataUrl}/>
          <Box marginX='auto'></Box>
          <IconButton color='error' onClick={onModalClose}>
            <CloseIcon/>
          </IconButton>
        </Stack>

        <Button variant='outlined' sx={{ width: 'fit-content' }} startIcon={<RefreshIcon/>} onClick={getAccounts}>
          Reload
        </Button>

        <Grid container spacing={2}>
          {children}
        </Grid>

        <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage} severity={toastSeverity}/>
      </Stack>
    </Modal>
  )
}

export default AccountsModal
